/* eslint-disable sonarjs/cognitive-complexity */
import {
  BaSeDataTable,
  BaSeOptionPicker,
  BaSeParagraph,
  BaSeShapeButton,
  BaSeSmall1,
  BaSeTheme,
  BaSeToggler,
} from '@base/react';
import * as Sebrae from '@sebrae/react-core';
import React from 'react';

import { useCityController } from '../../../../hooks/controllers/city-controller';
import { useUfController } from '../../../../hooks/controllers/uf-controller';

import { useMessage } from '../../../../hooks/messages/useMessage';
import { useRegionalService } from '../../../../hooks/services/regional-service';
import ListCityRegionl from './components/table/list-cities-regional';
import ListManagerRegionl from './components/table/list-manager-regional';
import NameRegional from './components/table/name-regional';
import OptionsRegional from './components/table/oprions-regional';
import UfRegional from './components/table/uf-regional';
import {
  AlignTitleColumn,
  Container,
  ContainerCustomFilter,
  UfCustomFilter,
} from './manage-regional-style';

const cache = Sebrae.StorageUtil.inTemporaryStore();
const cacheKeySebrae = '@empretec:sebrae';

function ManageRegional({ loading, fetchRefional, data }) {
  const { getUfs } = useUfController();
  const { hasRole } = Sebrae.useAuth();
  const { getCitiesByUf, rawCities } = useCityController();
  const { handleSuccess, handleError } = useMessage();
  const { updateClassEditPermission, updatePortalInterviewPermission } =
    useRegionalService();

  const [filters, setFilters] = React.useState({
    searchTerm: '',
    ufId: 0,
    sortType: 'ASC',
  });
  const [status, setStatus] = React.useState(false);

  const [listUfs, setListUfs] = React.useState([]);
  const [selectedUF, setSelectedUF] = React.useState([]);
  const [selectedCITY, setSelectedCITY] = React.useState([]);
  const [ufDefault, setUfDefault] = React.useState([]);

  const handleToggle = React.useCallback(
    (id, key) => {
      if (key === 'CLASS') {
        updateClassEditPermission(id)
          .then(() => {
            handleSuccess('Operação realizada com sucesso!');
            fetchRefional();
          })
          .catch((error) => {
            handleError(error.message ?? 'Erro!');
          });
      } else {
        updatePortalInterviewPermission(id)
          .then(() => {
            handleSuccess('Operação realizada com sucesso!');
            fetchRefional();
          })
          .catch((error) => {
            handleError(error.message ?? 'Erro!');
          });
      }
    },
    [
      handleError,
      handleSuccess,
      updateClassEditPermission,
      updatePortalInterviewPermission,
      fetchRefional,
    ],
  );

  const returnColor = React.useCallback((value) => {
    return value ? BaSeTheme.colors.institucionais.azulSebrae36 : '#617385';
  }, []);

  const ufDisabled = React.useMemo(() => {
    return cache.getItem(cacheKeySebrae) !== 'NA' && !hasRole('ADMINISTRADOR');
  }, [hasRole]);

  React.useEffect(() => {
    const load = async () => {
      try {
        const ufs = await getUfs();
        setListUfs(ufs);

        const UF = cache.getItem(cacheKeySebrae);

        const hasValidCache = cache.hasItem(cacheKeySebrae) && UF !== 'NA';

        if (hasValidCache) {
          const ufDef = ufs.find((uf) => uf.sigla === UF);

          if (ufDef) {
            await getCitiesByUf({ id: ufDef.id });
            setUfDefault([ufDef]);

            setFilters((prev) => {
              return {
                ...prev,
                ufId: ufDef.id,
              };
            });
          }
        }
      } catch (error) {
        setListUfs([]);
        console.error('Erro ao carregar UFs:', error);
      }
    };

    load();
  }, [getCitiesByUf, getUfs]);

  const buildCustomFilter = React.useCallback(
    (setFilter) => {
      return (
        <ContainerCustomFilter>
          <div style={{ display: 'grid' }}>
            <BaSeSmall1 isBold>Regionais desativadas</BaSeSmall1>
            <BaSeToggler
              value={status}
              onChange={() => {
                setStatus((prev) => {
                  setFilter((oldStatus) => {
                    if (!prev) {
                      return {
                        ...oldStatus,
                        active: false,
                      };
                    }
                    return {
                      ...oldStatus,
                      active: '',
                    };
                  });
                  return !prev;
                });
              }}
            />
          </div>

          <UfCustomFilter>
            {ufDisabled ? (
              <BaSeOptionPicker
                maxMenuHeight="100px"
                isDisabled
                values={ufDefault}
                key="UF:"
                maxOptionsSelected={1}
                initialSelectedValuesId={ufDefault.map((i) => i.id)}
                emptyValueLabel="UF:"
                onChange={(values) => {
                  if (values.length === 0) return;

                  setFilter((oldStatus) => {
                    return {
                      ...oldStatus,
                      ufId: values[0].id,
                    };
                  });
                }}
              />
            ) : (
              <BaSeOptionPicker
                maxMenuHeight="100px"
                values={listUfs}
                extraActions={[
                  {
                    action: () => {
                      setSelectedUF([]);
                      setSelectedCITY([]);
                      setFilter((prev) => {
                        return {
                          ...prev,
                          cityId: '',
                          ufId: '',
                        };
                      });
                    },
                    label: 'Limpar',
                  },
                ]}
                key="UF:"
                maxOptionsSelected={1}
                initialSelectedValuesId={selectedUF}
                emptyValueLabel="UF:"
                onChange={(values) => {
                  if (values.length === 0) return;
                  getCitiesByUf({ id: values[0].id });
                  setSelectedUF(values.map((val) => val.id));

                  setFilter((prev) => {
                    return {
                      ...prev,
                      ufId: values[0].id,
                    };
                  });
                }}
              />
            )}

            <BaSeOptionPicker
              maxMenuHeight="100px"
              maxOptionsSelected={1}
              values={rawCities}
              initialSelectedValuesId={selectedCITY}
              emptyValueLabel="Cidade"
              extraActions={[
                {
                  action: () => {
                    setSelectedCITY([]);
                    setFilter((prev) => {
                      return {
                        ...prev,
                        cityId: '',
                      };
                    });
                  },
                  label: 'Limpar',
                },
              ]}
              onChange={(values) => {
                if (values.length === 0) return;
                setSelectedCITY(values.map((val) => val.id));

                setFilter((prev) => {
                  return {
                    ...prev,
                    cityId: values[0].id,
                  };
                });
              }}
            />
          </UfCustomFilter>
        </ContainerCustomFilter>
      );
    },
    [
      getCitiesByUf,
      listUfs,
      rawCities,
      selectedCITY,
      selectedUF,
      status,
      ufDefault,
      ufDisabled,
    ],
  );

  return (
    <Container>
      <BaSeDataTable
        singularItemDescription="regional"
        pluralItemDescription="regionais"
        genderOfItemDescription="male"
        rowConfig={({ values }) => {
          if (!values.active) {
            return { backgroundColor: '#F3F5F7' };
          }
        }}
        config={[
          {
            accessorKey: 'REGIONAL_NAME',
            header: 'Nome da regional',
            id: 'REGIONAL_NAME',
            mapper: ({ values }) => (
              <NameRegional isActive={values?.active} name={values.name} />
            ),
          },
          {
            accessorKey: 'UF_INITIALS',
            id: 'UF_INITIALS',
            header: 'UF',
            mapper: ({ values }) => <UfRegional uf={values.uf.initials} />,
          },
          {
            accessorKey: 'CITY_NAME',
            id: 'CITY_NAME',
            header: 'Cidades',
            mapper: ({ values }) => (
              <ListCityRegionl listCities={values.cities} />
            ),
          },
          {
            accessorKey: 'MANAGER_NAME',
            id: 'MANAGER_NAME',
            header: 'Gestores vinculados',
            mapper: ({ values }) => (
              <ListManagerRegionl listManagers={values.responsibleManagers} />
            ),
          },

          {
            id: 'CAN_EDIT_CLASS',
            header: () => {
              return (
                <AlignTitleColumn>
                  <BaSeParagraph color="#939393">
                    Permitir edição de turma
                  </BaSeParagraph>

                  <BaSeShapeButton
                    size="small"
                    sizeIcon="small"
                    type="tertiary"
                    color="#0000C9"
                    nameIcon="exclamation-circle"
                    tooltip={{
                      size: 'small',
                      width: 200,
                      direction: 'top',
                      messageNoBreakLine: true,
                      color: '#617385',
                      message:
                        'Essa opção controla se os gestores regionais têm permissão para editar as turmas já criadas.',
                    }}
                  />
                </AlignTitleColumn>
              );
            },
            mapper: ({ values }) => (
              <BaSeToggler
                value={values.allowsClassEditing}
                color={returnColor(values.allowsClassEditing)}
                onChange={() => {
                  handleToggle(values.id, 'CLASS');
                }}
              />
            ),
          },

          {
            id: 'CAN_SHOW_CLASS_PORTAL',
            header: () => {
              return (
                <AlignTitleColumn>
                  <BaSeParagraph color="#939393">
                    Permitir entrevista do portal
                  </BaSeParagraph>

                  <BaSeShapeButton
                    size="small"
                    sizeIcon="small"
                    type="tertiary"
                    color="#0000C9"
                    nameIcon="exclamation-circle"
                    tooltip={{
                      size: 'small',
                      width: 200,
                      direction: 'top',
                      messageNoBreakLine: true,
                      color: '#617385',
                      message:
                        'Essa opção controla se o candidato pode ou não avançar para a entrevista após o cadastro no portal.',
                    }}
                  />
                </AlignTitleColumn>
              );
            },
            mapper: ({ values }) => (
              <BaSeToggler
                value={values.allowsPortalInterview}
                color={returnColor(values.allowsPortalInterview)}
                onChange={() => {
                  handleToggle(values.id, 'PORTAL');
                }}
              />
            ),
          },
          {
            header: 'Opções',
            mapper: ({ values, reloadData }) => (
              <OptionsRegional
                fullData={values}
                reloadData={reloadData}
                isActive={values?.active}
                id={values.id}
                name={values.name}
              />
            ),
          },
        ]}
        columnConfig={[
          {
            columnId: 'UF_INITIALS',
            stickyOnCompactMode: true,
          },
          {
            columnId: 'citys',
            stickyOnCompactMode: true,
          },
          {
            columnId: 'reginalName',
            stickyOnCompactMode: true,
          },
        ]}
        fetchData={fetchRefional}
        backgroundColor={BaSeTheme.colors.defaultColors.white}
        foregroundColor={BaSeTheme.colors.institucionais.cinzaSebrae60}
        highlightedColor={BaSeTheme.colors.institucionais.cinzaSebrae30}
        separatorColor={BaSeTheme.colors.institucionais.cinzaSebrae90}
        filterAreaBackgroundColor={BaSeTheme.colors.defaultColors.white}
        hasPagination
        hasSorting
        hasCustomFilter
        items={data.items}
        total={data.total}
        isLoading={loading}
        initialSortType="ASC"
        initialSortField="REGIONAL_NAME"
        buildCustomFilter={buildCustomFilter}
        customFilterVisible
        configActionItemsProps={{ icon: 'ellipsis-v' }}
        textFilterOnTyping
        hasTextFilter
        textFilterWidth={345}
        textFilterPlaceholder={`Pesquisar por nome`}
        customFilterTitle=""
        textFilterAlwaysVisible
        onTextFilter={({ setFilter, term }) => {
          setFilter((currentFilter) => {
            setFilters((prev) => {
              return {
                ...currentFilter,
                ...prev,
                searchTerm: term,
              };
            });
            return {
              ...currentFilter,
              ...filters,
              searchTerm: term,
            };
          });
        }}
        renderCustomFilter={({ setFilter }) => buildCustomFilter(setFilter)}
        customFilterIconVisible
        densityLevel="normal"
        footerBackgroundColor="#ffffff"
        hasSelectOrUnselectAll
        hasToggleAllSelection
        headerBackgroundColor="#ffffff"
        itemsPerPage={[5, 10, 15, 50, 100]}
        selectOrUnselectAllBackgroundColor="#f5f7fa"
        shadow=" 0  4px  8px 0 rgba(20, 46, 82, 0.2)"
        sortingAscIcon="arrow-head-down"
        sortingDescIcon="arrow-head-up"
        tableHeaderBackgroundColor="transparent"
        textColor="#394d60"
        titleColor="#617385"
        onClearFilter={({ resetSelection }) => {
          fetchRefional({
            filter: {
              searchTerm: '',
              ufId: '',
            },
            page: {
              size: 10,
              index: 0,
            },
            sort: { type: 'ASC' },
          });
          resetSelection();
        }}
      />
    </Container>
  );
}

export default ManageRegional;
